import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureFlags } from "@/hooks/useFeatureFlag";
import { useSiteFeatures } from "@/hooks/useSiteFeatures";
import { ALL_AI_FEATURE_KEYS, normalizePlanFeatures, type AiPlanFeatureKey } from "@/lib/ai-plan-features";

export function useAiPlanFeatures() {
  const { profile } = useAuth();
  const hasPlanId = Boolean(profile?.plan_id);
  const planExpiresAt = profile?.plan_expires_at ? new Date(profile.plan_expires_at).getTime() : null;
  const isPlanExpired = planExpiresAt !== null && planExpiresAt <= Date.now();
  const { data: rolloutFlags } = useFeatureFlags();
  const { toofiEnabled } = useSiteFeatures();

  const { data: featureFlags } = useQuery<string[] | null>({
    queryKey: ["ai-plan-features", profile?.plan_id],
    enabled: hasPlanId && !isPlanExpired,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ai_plans")
        .select("feature_flags")
        .eq("id", profile!.plan_id)
        .maybeSingle();
      if (error) return null;
      return (data as { feature_flags?: string[] | null } | null)?.feature_flags ?? null;
    },
    staleTime: 60_000,
  });

  const planFeatureSet = useMemo(
    () => (hasPlanId && !isPlanExpired ? normalizePlanFeatures(featureFlags) : new Set<AiPlanFeatureKey>()),
    [featureFlags, hasPlanId, isPlanExpired],
  );
  const overrideFeatureSet = useMemo(() => {
    if (!rolloutFlags) return new Set<AiPlanFeatureKey>();
    const enabled = ALL_AI_FEATURE_KEYS.filter((key) => rolloutFlags[key]);
    return new Set<AiPlanFeatureKey>(enabled);
  }, [rolloutFlags]);
  const effectiveSet = useMemo(
    () => (toofiEnabled ? new Set<AiPlanFeatureKey>([...planFeatureSet, ...overrideFeatureSet]) : new Set<AiPlanFeatureKey>()),
    [overrideFeatureSet, planFeatureSet, toofiEnabled],
  );

  const hasFeature = (key: AiPlanFeatureKey) => effectiveSet.has(key);

  return {
    isPlanExpired,
    hasPlanId,
    featureFlags: Array.from(effectiveSet),
    hasFeature,
  };
}
